import { useContext, useEffect, useRef } from 'react';
import { NavigationContext } from '@react-navigation/native';

/**
 * Re-runs `refresh` whenever the hosting screen regains focus.
 *
 * Skips the first focus event so the caller's mount load is not doubled.
 * Subsequent focuses (returning from create/edit/details) reload data.
 *
 * Safe outside a navigator: when NavigationContext is missing (e.g. isolated
 * hook unit tests), this is a no-op.
 */
export function useRefreshOnFocus(
  refresh: () => void,
  enabled = true,
): void {
  const navigation = useContext(NavigationContext);
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  const isFirstFocus = useRef(true);

  useEffect(() => {
    if (!enabled || !navigation) return undefined;

    // Only react to focus events. The first event (initial mount focus) is
    // skipped so it does not duplicate the caller's autoLoad fetch. Do not
    // pre-clear isFirstFocus via isFocused() — React Navigation also emits
    // focus on mount, and clearing early would make that emit refresh.
    return navigation.addListener('focus', () => {
      if (isFirstFocus.current) {
        isFirstFocus.current = false;
        return;
      }
      refreshRef.current();
    });
  }, [enabled, navigation]);
}
